import React, { useMemo } from 'react';
import { Select } from 'antd';
import {
  CreatePromotionModel,
  PromotionItem,
  PromotionTypeLocal,
  ReferencesSectionLocal,
} from 'domains/promotion/Promotion.types';
import { Flex, MarginBox, Text } from 'UI';
import { compareArrays } from 'utils';
import { DefaultTag, DisabledTag, ErrorTag } from '../CreatePromotion.styled';
import { hasLocalReferenceDuplicates } from './PromotionErrorNotifications';

interface CustomTagProps {
  label: React.ReactNode;
  onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

const TagComponent = ({
  promotionType,
  label,
  item,
  isRemovable,
  onClose,
}: Readonly<{
  promotionType: PromotionTypeLocal;
  label: React.ReactNode;
  item: PromotionItem | undefined;
  isRemovable: boolean;
  onClose: () => void;
}>) => {
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  if (!isRemovable) {
    return (
      <DisabledTag onMouseDown={onPreventMouseDown} closable={isRemovable} onClose={onClose}>
        <Text type={'text'}>{label}</Text>
      </DisabledTag>
    );
  }

  if (item && promotionType !== 'BANNER' && (item.alreadyPromoted || item.alreadyLocallyPromoted)) {
    return (
      <ErrorTag onMouseDown={onPreventMouseDown} closable={isRemovable} onClose={onClose}>
        <Text type={'text'}>{label}</Text>
      </ErrorTag>
    );
  }

  return (
    <DefaultTag onMouseDown={onPreventMouseDown} closable={isRemovable} onClose={onClose}>
      <Text type={'text'}>{label}</Text>
    </DefaultTag>
  );
};

function updateLastLocallyPromotedReference(
  sections: ReferencesSectionLocal[],
  removedCode: string | undefined,
): ReferencesSectionLocal[] {
  if (!removedCode) return sections;
  const occurrences = sections.reduce(
    (count, section) => count + section.items.filter((item) => item.item === removedCode).length,
    0,
  );
  if (occurrences === 1) {
    return sections.map((section) => ({
      ...section,
      items: section.items.map((item) =>
        item.item === removedCode ? { ...item, alreadyLocallyPromoted: false } : item,
      ),
    }));
  }
  return sections;
}

export function applyUpdateReferences(
  promotion: CreatePromotionModel,
  newItems: string[],
  index: number,
): CreatePromotionModel {
  const currentSections = promotion.references ?? [{ items: [], discount: 0 }];
  const currentCodes = currentSections[index]?.items ?? [];
  const addedItem = newItems.find((item) => !currentCodes.some((code) => code.item === item));
  const removedItem = currentCodes.find((code) => !newItems.includes(code.item));
  const isDuplicate =
    !!addedItem &&
    hasLocalReferenceDuplicates(
      addedItem,
      currentSections.filter((_, i) => i !== index),
    );

  const updatedSections = currentSections.map((section, i) => {
    if (i !== index) return section;
    let items = currentCodes.filter((code) => code.item !== removedItem?.item);
    if (addedItem) {
      items = [...items, { item: addedItem, removable: true, alreadyLocallyPromoted: isDuplicate }];
    }
    return { ...section, items };
  });
  const finalSections = updateLastLocallyPromotedReference(updatedSections, removedItem?.item);
  return { ...promotion, references: finalSections };
}

export const ReferencesInput = ({
  promotion,
  setPromotion,
}: {
  promotion: CreatePromotionModel;
  setPromotion: (x: CreatePromotionModel) => void;
}) => {
  const referencesSection = useMemo(() => {
    return promotion.references && promotion.references.length > 0
      ? promotion.references
      : [{ items: [], discount: 0 }];
  }, [promotion]);

  return (
    <Flex direction={'column'} size={4}>
      <MarginBox mt={30} />
      {referencesSection.map((section: ReferencesSectionLocal, index: number) => {
        const inputValue = section?.items.map((item) => item.item);
        return (
          <React.Fragment key={'promotion_references_' + index}>
            <MarginBox mt={20} />
            <Select
              mode={'tags'}
              data-cy={'by-references-input'}
              value={inputValue}
              open={false}
              tagRender={(props: CustomTagProps) => {
                const { label, onClose } = props;
                const item = promotion.references
                  ? promotion.references[index].items.find((code) => code.item === label)
                  : undefined;
                const isRemovable = item ? item.removable : true;
                return (
                  <TagComponent
                    promotionType={promotion.promotionType}
                    label={label}
                    item={item}
                    isRemovable={isRemovable}
                    onClose={onClose}
                  />
                );
              }}
              onChange={(newItems: string[]) => {
                const upperCaseItems = newItems.map((x) => x.toUpperCase());
                const filteredItems = upperCaseItems.filter((item, idx) => upperCaseItems.indexOf(item) === idx);
                if (compareArrays(upperCaseItems, filteredItems)) {
                  setPromotion(applyUpdateReferences(promotion, filteredItems, index));
                }
              }}
              tokenSeparators={[' ', ',']}
              placeholder={''}
              bordered
            />
          </React.Fragment>
        );
      })}
    </Flex>
  );
};
